import { supabaseClient } from "@/lib/supabase";
import { queryOptions, useQuery } from "@tanstack/react-query";

export interface ValidationHistoryEntryT {
    validation_id: string;
    is_valid: boolean | null;
    validated_at: string | null;
    created_at: string;
    reason: string | null;
    on_behalf_of_name: string | null;
    on_behalf_of_organisation: string | null;
    validated_by_name: string | null;
    validated_by_email: string | null;
}

const historyColumns =
    "validation_id, is_valid, validated_at, created_at, reason, on_behalf_of_name, on_behalf_of_organisation, profiles(first_name, last_name, email)";

function mapHistoryRow(validation: {
    validation_id: string;
    is_valid: boolean | null;
    validated_at: string | null;
    created_at: string;
    reason: string | null;
    on_behalf_of_name: string | null;
    on_behalf_of_organisation: string | null;
    profiles: { first_name: string | null; last_name: string | null; email: string | null } | null;
}): ValidationHistoryEntryT {
    const name = [validation.profiles?.first_name, validation.profiles?.last_name].filter(Boolean).join(" ").trim();
    return {
        validation_id: validation.validation_id,
        is_valid: validation.is_valid,
        validated_at: validation.validated_at,
        created_at: validation.created_at,
        reason: validation.reason,
        on_behalf_of_name: validation.on_behalf_of_name,
        on_behalf_of_organisation: validation.on_behalf_of_organisation,
        validated_by_name: name || null,
        validated_by_email: validation.profiles?.email ?? null,
    };
}

/**
 * Every validation a form has ever had, newest first - not just the current one.
 *
 * Queries from `validations` (not `form_validations`) so `created_at` is a real top-level
 * column to order by. form_validations -> validations is a to-one embed per row, and
 * PostgREST's `foreignTable` order option only orders elements *within* a to-many embedded
 * array - it can't reorder the outer rows by an embedded column, which is what's needed here.
 */
export async function selectFormValidationHistory(formId: string): Promise<ValidationHistoryEntryT[]> {
    const { data, error } = await supabaseClient
        .from("validations")
        .select(`${historyColumns}, form_validations!inner(form_id)`)
        .eq("form_validations.form_id", formId)
        .order("created_at", { ascending: false });
    if (error) throw error;
    return (data ?? []).map((row) => mapHistoryRow(row));
}

export const formValidationHistoryQueryOptions = (formId: string, enabled: boolean) =>
    queryOptions({
        queryKey: ["validations", "history", "form", formId],
        queryFn: () => selectFormValidationHistory(formId),
        enabled: Boolean(formId) && enabled,
    });

export function useFormValidationHistoryQuery(formId: string, enabled: boolean) {
    return useQuery(formValidationHistoryQueryOptions(formId, enabled));
}

/** Every validation a schedule has ever had, newest first - not just the current one. See selectFormValidationHistory for why the query shape is what it is. */
export async function selectScheduleValidationHistory(scheduleId: string): Promise<ValidationHistoryEntryT[]> {
    const { data, error } = await supabaseClient
        .from("validations")
        .select(`${historyColumns}, schedule_validations!inner(schedule_id)`)
        .eq("schedule_validations.schedule_id", scheduleId)
        .order("created_at", { ascending: false });
    if (error) throw error;
    return (data ?? []).map((row) => mapHistoryRow(row));
}

export const scheduleValidationHistoryQueryOptions = (scheduleId: string, enabled: boolean) =>
    queryOptions({
        queryKey: ["validations", "history", "schedule", scheduleId],
        queryFn: () => selectScheduleValidationHistory(scheduleId),
        enabled: Boolean(scheduleId) && enabled,
    });

export function useScheduleValidationHistoryQuery(scheduleId: string, enabled: boolean) {
    return useQuery(scheduleValidationHistoryQueryOptions(scheduleId, enabled));
}
